import { useState } from "react";

/**
 * Playable demo arcade for the landing page. Each tab embeds a real, standalone web game
 * (served from the repo's `Demos/` folder under `/demos/*`) in an iframe. The games talk to
 * Laddr over the same origin (guest token + live WebSocket leaderboard), so a visitor can set a
 * score and watch it appear on the board with no sign-up. `ready: false` games show a placeholder
 * until their files land.
 */
type Game = { id: string; label: string; genre: string; ready: boolean };

const GAMES: Game[] = [
  { id: "quickdraw", label: "Quickdraw", genre: "Reflex", ready: true },
  { id: "skyhop", label: "Finhop", genre: "Arcade", ready: true },
  { id: "merge2048", label: "Merge 2048", genre: "Puzzle", ready: true },
  { id: "typerush", label: "TypeRush", genre: "Typing", ready: true },
  { id: "cointap", label: "CoinTap", genre: "Idle", ready: true },
  { id: "racing", label: "Time Trial", genre: "Racing", ready: true },
  { id: "tenten", label: "1010!", genre: "Puzzle", ready: true },
  { id: "dailyseed", label: "Daily Seed", genre: "Daily", ready: true },
  { id: "factionwar", label: "Faction War", genre: "Team", ready: true },
];

export function DemoArcade() {
  const [active, setActive] = useState(GAMES.find((g) => g.ready)?.id ?? GAMES[0].id);
  const game = GAMES.find((g) => g.id === active)!;
  return (
    <section className="section demo-arcade" id="play">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Play it live</span>
          <h2>Real games. A live leaderboard each.</h2>
          <p>
            Every game here is a tiny web game wired to Laddr. Set a score and watch it appear on
            the board in real time — same as your players would. No sign-up; play right here.
          </p>
        </div>

        <div className="arcade-tabs" role="tablist">
          {GAMES.map((g) => (
            <button
              key={g.id}
              role="tab"
              aria-selected={g.id === active}
              className={`arcade-tab${g.id === active ? " active" : ""}${g.ready ? "" : " soon"}`}
              onClick={() => g.ready && setActive(g.id)}
              disabled={!g.ready}
            >
              <span className="at-label">{g.label}</span>
              <span className="at-genre">{g.ready ? g.genre : "soon"}</span>
            </button>
          ))}
        </div>

        <div className="arcade-frame">
          {game.ready ? (
            // key forces a fresh iframe (clean game state) when switching tabs.
            <iframe key={game.id} title={game.label} src={`/demos/${game.id}/`} className="arcade-iframe" />
          ) : (
            <div className="arcade-soon">Coming soon…</div>
          )}
        </div>
      </div>
    </section>
  );
}
